import { TEAM_PLANET_POPULATION_CAPACITY } from "./teamPlanets.js";
/**
 * Sun control and round victory tuning — see
 * `@docs/plans/sun-control-and-round-victory.md` and
 * `docs/game-details/alliance_system.md`. The pure rules that consume these
 * live in `packages/game-engine/src/sunControl.ts` (claims, garrisons,
 * reclaims) and `packages/game-engine/src/roundVictory.ts` (the seven-Sun
 * win, Endgame, Death Match). Dark Matter rewards live in `premium.ts` with
 * every other price and grant.
 */
/** Garrison population that must land at a Sun inside the claim window for the claim to become control. */
export const SUN_CONTROL_CLAIM_POPULATION = 100000;
/**
 * Garrison floor. Only the surplus above it may be recalled, and a
 * stronghold that leaves the garrison below it has
 * {@link SUN_CONTROL_TOP_UP_WINDOW_HOURS} to restore it or control lapses.
 */
export const SUN_CONTROL_MAINTAIN_POPULATION = 50000;
/** Most population one Sun's garrison may hold — deliberately the same number as the Team planet's capacity. */
export const SUN_CONTROL_LEDGER_CAP = TEAM_PLANET_POPULATION_CAPACITY;
/** After the battle that granted a claim, the garrison has this long to reach {@link SUN_CONTROL_CLAIM_POPULATION}. */
export const SUN_CONTROL_CLAIM_WINDOW_HOURS = 12;
/** After a stronghold that left the garrison under the floor, it has this long to be restored. */
export const SUN_CONTROL_TOP_UP_WINDOW_HOURS = 12;
/** No reclaim may be dispatched at a Sun until this long after its garrison took control. */
export const SUN_CONTROL_RECLAIM_IMMUNITY_HOURS = 12;
/** Controlled Suns an alliance needs to win the round. */
export const SUNS_TO_WIN = 7;
/** Once any alliance controls this many Suns, joining an alliance is frozen universe-wide for the rest of the round. */
export const SUN_CONTROL_MEMBERSHIP_FREEZE_COUNT = 5;
/** Countdown that starts when an alliance reaches {@link SUNS_TO_WIN}; only reclaims of its Suns may be dispatched meanwhile. */
export const SUN_VICTORY_CONTEST_HOURS = 48;
/** An attack inbound to an uncontrolled Sun is announced to players with a planet within this many systems, same galaxy only. */
export const SUN_ATTACK_NOTIFICATION_SYSTEM_RADIUS = 6;
/** Intelligence level at which a Sun attack notification shows the arrival time. */
export const SUN_ATTACK_ETA_INTELLIGENCE_LEVEL = 10;
/** Intelligence level, researched on the controlling alliance's Team planet, that hides who controls its Suns. */
export const SUN_CONTROL_CONCEALMENT_INTELLIGENCE_LEVEL = 20;
/** Espionage against a concealed Sun with Intelligence equal to the Team planet's reveals the controller this often. */
export const ESPIONAGE_SUN_TIE_REVEAL_CHANCE = 0.2;
/** Cumulative population that has fought in Sun battles this round at which the round enters its Endgame. */
export const ROUND_ENDGAME_SUN_BATTLE_POPULATION_THRESHOLD = 20000000;
/** Resource, speed and flight multipliers during Death Match. */
export const DEATH_MATCH_MULTIPLIER = 20;
/**
 * Days of Death Match before the universe is ready to reset. The reset
 * itself is an operator-run script (`apps/functions/src/scripts/resetUniverse.ts`)
 * — the plan allows three to five days, so this is the earliest it may run.
 */
export const DEATH_MATCH_DAYS = 3;
/** Round lengths are configured in months; a month is this many days. */
export const ROUND_MONTH_DAYS = 30;
/**
 * Default `endgameThresholdMonths` per universe speed, used when the
 * universe doesn't configure its own: roughly a year at 1x, six months at
 * 3x–5x and three months at 10x and above. Checked in order; the first row
 * whose `minSpeed` the universe's `speedMultiplier` reaches wins.
 */
export const DEFAULT_ENDGAME_THRESHOLD_MONTHS = [
    { minSpeed: 10, months: 3 },
    { minSpeed: 3, months: 6 },
    { minSpeed: 0, months: 12 },
];
/**
 * Default `endgameCountdownMonths` per universe speed. Provisional — the plan
 * leaves the countdown length to per-universe configuration.
 */
export const DEFAULT_ENDGAME_COUNTDOWN_MONTHS = [
    { minSpeed: 10, months: 1 },
    { minSpeed: 3, months: 2 },
    { minSpeed: 0, months: 3 },
];
/** Alliances listed in a round's public final standings. */
export const ROUND_STANDINGS_LIMIT = 10;
//# sourceMappingURL=sunControl.js.map